import { Maybe } from '../../../../Maybe'
import { Address, ShippingAlias } from '../../Address'
import {
  ClientConsentMap,
  ClientConsentMapUpdate,
  ClientConsentMapUpload,
} from './Consent'
import { InsuranceType } from '../../../../screens/Page/secure/client/LeftColumn/Insurance/InsuranceForm'

export interface ClientProfile {
  readonly firstName: string
  readonly lastName: string
  readonly consent: ClientConsentMap
  readonly dateOfBirth: string
  readonly emailAddress: string
  readonly gender: 'male'|'female'|'other'|'undisclosed'
  readonly hasPhysician: boolean
  readonly insurance: ClientInsuranceDownload
  readonly knumber: string
  readonly language: 'en-CA'|'fr-CA'
  readonly telephoneNumber: string
  readonly physicalAddress: Address
  readonly mailingAddress: Address
  readonly shippingAddress: Address
  readonly startDate: string
  readonly endDate: string
  readonly documentUploads?: Array<{
    id: string
    uuid: string
    documentType: string
    statusCode: string
    createdOn: string
  }>
  readonly activePolicyPeriod?: {
    remainingQuantity: number
    startDate: string
    endDate: string
    policyType: string
  }
  readonly currentCoverage?: {
    effectiveDate: string
    expiryDate: string
    policyType: string
  }
}

export interface ClientProfileAmendment {
  readonly firstName?: Maybe<string>
  readonly lastName?: Maybe<string>
  readonly physicalAddress?: Maybe<Address>
  readonly mailingAddress?: Maybe<Address>
  readonly shippingAddress?: Maybe<Address>
  readonly shippingAlias?: Maybe<ShippingAlias>
}

export interface ClientProfileDownload {
  readonly firstName: string
  readonly lastName: string
  readonly consent2: ClientConsentMap
  readonly dateOfBirth: string
  readonly emailAddress: string
  readonly gender: 'male'|'female'|'other'|'undisclosed'
  readonly hasPhysician: boolean
  readonly insurance: ClientInsuranceDownload
  readonly knumber: string
  readonly language: 'en-CA'|'fr-CA'
  readonly telephoneNumber: string
  readonly physicalAddress: Address
  readonly mailingAddress: Address
  readonly shippingAddress: Address
  readonly startDate: string
  readonly endDate: string
  readonly documentUploads?: Array<{
    id: string
    uuid: string
    documentType: string
    statusCode: string
    createdOn: string
  }>
  readonly activePolicyPeriod?: {
    remainingQuantity: number
    startDate: string
    endDate: string
    policyType: string
  }
  readonly currentCoverage?: {
    effectiveDate: string
    expiryDate: string
    policyType: string
  }
}

export interface ClientProfileUpdate {
  readonly consent?: ClientConsentMapUpdate
  readonly emailAddress?: string
  readonly telephoneNumber?: string
}
export interface ClientProfileUpload {
  readonly firstName: string
  readonly lastName: string
  readonly acknowledgement: boolean
  readonly consent: ClientConsentMapUpload
  readonly dateOfBirth: string
  readonly emailAddress: string
  readonly gender: 'male'|'female'|'other'|'undisclosed'
  readonly hasPhysician: boolean
  readonly knumber?: string
  readonly language: 'en-CA'|'fr-CA'
  readonly telephoneNumber: string
  readonly physicalAddress: Address
  readonly mailingAddress: Address
  readonly shippingAddress: Address
  readonly startDate: string
  readonly endDate: string
  readonly documentUploads?: Array<{
    id: string
    uuid: string
    documentType: string
    statusCode: string
    createdOn: string
  }>
  readonly activePolicyPeriod?: {
    remainingQuantity: number
    startDate: string
    endDate: string
    policyType: string
  }
  readonly currentCoverage?: {
    effectiveDate: string
    expiryDate: string
    policyType: string
  }
}

export interface ClientProfileRenewal {
  readonly firstName: string
  readonly lastName: string
  readonly acknowledgement?: boolean
  readonly dateOfBirth: string
  readonly knumber?: string
  readonly gender: 'male'|'female'|'other'|'undisclosed'
  readonly insurance?: ClientInsuranceUpload
  readonly physicalAddress: Address
  readonly mailingAddress: Address
  readonly shippingAddress: Address
  readonly consent?: ClientConsentMap
  readonly documentUploads?: Array<{
    id: string
    uuid: string
    documentType: string
    statusCode: string
    createdOn: string
  }>
  readonly activePolicyPeriod?: {
    remainingQuantity: number
    startDate: string
    endDate: string
    policyType: string
  }
}

export interface PriorAuthorization{
  startDate?: string
  endDate?: string
}

export enum InsuranceTypeCrm {
  Manual = 'Manual direct billing',
  GroupPolicy = 'Electronic direct billing'
}

export interface ClientInsuranceUpload {
  insuranceProviderUUID: string
  groupPlanNumber: string
  certificateNumber: string
  cardholderId: string
  relationshipToPolicyHolder: string
  priorAuthorizationRequired: boolean
  priorAuthorization?: PriorAuthorization
}

export interface ClientManualInsuranceUpload {
  insuranceProviderUUID?: string
  priorAuthorizationRequired: boolean
  text?: string
}

export interface ClientInsuranceCoverage {
  insuranceCoverageUUID: string
  status: string
  relationshipToPolicyHolder: string
  cardholderId: string
  certificateNumber: string
  insuranceProvider: {
    name: string
  }
  groupPlan: {
    groupPlanNumber: string
    name: string
  }
  insurancePolicyGroupNumber: string
  insuranceType?: InsuranceTypeCrm | string
  insuranceProviderText?: string
}

export interface ClientInsuranceDownload {
  period?: {
    allowanceRemaining: number
    endDate: string
    startDate: string
  }
  private?: {
    insuranceCoversMedication: boolean
    insuranceCoversCannabis: boolean
    insuranceCoverageUUID?: string
    otherPlan?: {
      number?: string
      provider?: string
      providerName?: string
    }
    yourPlan?: {
      number?: string
      provider?: string
      providerName?: string
    }
  }
  vac?: {
    kNumber: string
  }
}

export interface InsuranceKeys {
  insuranceCoverageUUID?: string
  kNumber?: string
}

export interface IInsuranceCoverage {
  insuranceType: InsuranceType
  insuranceCoverageUUID?: string
  relationshipToPolicyHolder?: string
  insuranceProviderName?: string
  insuranceProviderManualName?: string
  insuranceProviderText?: string
  groupPlanNumber?: string
  certificateNumber?: string
  cardholderId?: string
  kNumber?: string
  isVeteran?: boolean
  directBilling?: 'true' | 'false'
  documentUploads?: Array<{
    id: string
    uuid: string
    documentType: string
    statusCode: string
    createdOn: string
  }>
  activePolicyPeriod?: {
    remainingQuantity: number
    startDate: string
    endDate: string
    policyType: string
  }
  currentCoverage?: {
    effectiveDate: string
    expiryDate: string
    policyType: string
  }
}
